/* Product-page mobile image viewer. Only the main product gallery and its
 * selected laser artwork are eligible. No magnifiers or page-wide image/link
 * enhancement. Original files load after a deliberate tap; the viewer alone
 * owns pinch zoom. The v3.5.1 fullscreen/gesture implementation is unchanged.
 */
(() => {
  'use strict';
  if (window.MJLImageViewer) return;
  const mobile = matchMedia('(max-width: 767px), (hover: none) and (pointer: coarse)');
  // The server emits this marker only for the main product-page gallery,
  // never for featured products, recommendation cards or collection images.
  const galleryScope = 'main mjl-product-gallery[data-mjl-product-page-media]';
  const scope = `${galleryScope} [data-gallery-stage] img, ${galleryScope} [data-artwork-frame] img`;
  const records = new Map();
  let viewer, observer, scanFrame;
  let ignoreClickUntil = 0;
  const pagePointers = new Map();
  const dirty = new Set();

  const clamp = (n, min, max) => Math.max(min, Math.min(n, max));
  const remember = (node, properties) => properties.map(name => [name, node.style.getPropertyValue(name), node.style.getPropertyPriority(name)]);
  const restore = (node, values) => values.forEach(([name, value, priority]) => value ? node.style.setProperty(name, value, priority) : node.style.removeProperty(name));

  function safeURL(raw) {
    if (typeof raw !== 'string' || !raw.trim()) return null;
    try { const url = new URL(raw, location.href); return ['http:', 'https:'].includes(url.protocol) ? url : null; }
    catch (_) { return null; }
  }
  function originalURL(raw) {
    const url = safeURL(raw);
    if (!url) return '';
    if (url.hostname === 'cdn.shopify.com' || /(^|\.)shopifycdn\.(com|net)$/.test(url.hostname) ||
        (url.origin === location.origin && /^\/cdn\/(shop|s)\//.test(url.pathname))) {
      // Keep version/cache and format parameters, but request the original image
      // instead of magnifying a 144px/720px thumbnail or a cropped derivative.
      ['width', 'height', 'crop', 'pad'].forEach(key => url.searchParams.delete(key));
    }
    return url.href;
  }
  function sourceFor(img) {
    const gallery = img.closest('mjl-product-gallery');
    const stage = img.closest('[data-gallery-stage]');
    const source = img.dataset.mjlFullSrc || (stage && gallery?.current?.type === 'image' ? gallery.current.src : '') ||
      img.currentSrc || img.getAttribute('src');
    return {
      src: originalURL(source), preview: safeURL(img.currentSrc || img.getAttribute('src'))?.href || '',
      alt: img.alt || gallery?.dataset.productTitle || 'Image preview'
    };
  }

  class ImageViewer {
    constructor() {
      this.scale = 1; this.x = 0; this.y = 0; this.revision = 0;
      this.pointers = new Map();
      this.dialog = document.createElement('dialog');
      this.dialog.className = 'mjl-image-viewer';
      this.dialog.setAttribute('aria-label', 'Full-screen image viewer');
      this.dialog.innerHTML = '<div class="mjl-image-viewer__bar"><span class="mjl-image-viewer__title">Image preview</span><button type="button" data-image-close aria-label="Close image viewer"><span aria-hidden="true">&times;</span> Close</button></div>' +
        '<div class="mjl-image-viewer__stage" tabindex="0" role="group" aria-label="Image: pinch to zoom, then drag to move"><img class="mjl-image-viewer__image" alt="" draggable="false"></div>' +
        '<div class="mjl-image-viewer__bottom"><div class="mjl-image-viewer__tools" role="group" aria-label="Image zoom"><button type="button" data-image-out aria-label="Zoom out">&minus;</button><button type="button" data-image-reset aria-label="Reset zoom to fit image">100%</button><button type="button" data-image-in aria-label="Zoom in">+</button></div><p class="mjl-image-viewer__hint">Pinch to zoom &middot; Drag to move &middot; Double-tap to reset or zoom</p><span class="mjl-image-viewer__status" role="status" aria-live="polite"></span></div>';
      this.stage = this.dialog.querySelector('.mjl-image-viewer__stage');
      this.img = this.dialog.querySelector('img');
      this.status = this.dialog.querySelector('[role="status"]');
      this.closeButton = this.dialog.querySelector('[data-image-close]');
      this.resetButton = this.dialog.querySelector('[data-image-reset]');
      this.outButton = this.dialog.querySelector('[data-image-out]');
      this.inButton = this.dialog.querySelector('[data-image-in]');
      document.body.append(this.dialog);
      this.closeButton.addEventListener('click', () => this.close());
      this.resetButton.addEventListener('click', () => this.reset());
      this.outButton.addEventListener('click', () => this.zoomAt(this.scale / 1.5));
      this.inButton.addEventListener('click', () => this.zoomAt(this.scale * 1.5));
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.close(); });
      this.dialog.addEventListener('close', () => this.cleanup());
      this.dialog.addEventListener('click', event => { if (event.target === this.dialog) this.close(); });
      this.dialog.addEventListener('keydown', event => this.key(event));
      this.stage.addEventListener('pointerdown', event => this.down(event));
      this.stage.addEventListener('pointermove', event => this.move(event));
      ['pointerup', 'pointercancel', 'lostpointercapture'].forEach(type => this.stage.addEventListener(type, event => this.up(event)));
      this.stage.addEventListener('dblclick', event => { event.preventDefault(); if (!this.touchTapAt || performance.now() - this.touchTapAt > 700) this.doubleTap(this.point(event)); });
      this.stage.addEventListener('dragstart', event => event.preventDefault());
      if ('ResizeObserver' in window) new ResizeObserver(() => this.fit()).observe(this.stage);
      else window.addEventListener('resize', () => this.fit(), { passive:true });
    }
    open(source, trigger) {
      if (!source.src) return;
      if (this.opened) this.close();
      // Native close dispatch can be queued. Finish cleanup before reuse.
      if (this.lock) this.cleanup();
      this.opened = true; this.trigger = trigger; this.revision++;
      const revision = this.revision;
      this.reset(); this.naturalWidth = 0; this.naturalHeight = 0;
      this.img.alt = source.alt; this.img.hidden = false;
      this.status.textContent = 'Loading full-size image...';
      this.dialog.dataset.loading = 'true';
      const body = document.body, html = document.documentElement;
      this.lock = {
        x:window.scrollX, y:window.scrollY,
        body:remember(body, ['position', 'top', 'left', 'right', 'width', 'overflow']),
        html:remember(html, ['overflow', 'scroll-behavior'])
      };
      body.style.setProperty('position', 'fixed'); body.style.setProperty('top', `${-this.lock.y}px`);
      body.style.setProperty('left', `${-this.lock.x}px`); body.style.setProperty('right', '0');
      body.style.setProperty('width', '100%'); body.style.setProperty('overflow', 'hidden');
      html.style.setProperty('overflow', 'hidden'); html.style.setProperty('scroll-behavior', 'auto');
      if (typeof this.dialog.showModal === 'function') {
        this.native = true; this.dialog.showModal();
      } else {
        this.native = false; this.dialog.setAttribute('open', '');
        this.dialog.setAttribute('role', 'dialog'); this.dialog.setAttribute('aria-modal', 'true');
        this.inertSiblings = Array.from(body.children).filter(node => node !== this.dialog).map(node => [node, node.inert]);
        this.inertSiblings.forEach(([node]) => { node.inert = true; });
      }
      this.closeButton.focus({ preventScroll:true });
      const adoptDimensions = () => {
        if (!this.opened || this.revision !== revision || !this.img.naturalWidth) return;
        this.naturalWidth = this.img.naturalWidth; this.naturalHeight = this.img.naturalHeight; this.fit();
      };
      this.img.onload = adoptDimensions;
      this.img.onerror = () => {
        if (!this.opened || this.revision !== revision) return;
        this.img.hidden = true; this.status.textContent = 'This image could not be loaded. Close the viewer and try again.';
      };
      // Show the already-loaded preview while fetching the original. No original
      // images are prefetched on page load or merely when a card scrolls into view.
      this.img.src = source.preview || source.src;
      if (this.img.complete) adoptDimensions();
      const full = new Image(); this.full = full; full.decoding = 'async';
      full.onload = () => {
        if (!this.opened || this.revision !== revision) return;
        this.img.src = source.src; this.img.hidden = false;
        this.naturalWidth = full.naturalWidth; this.naturalHeight = full.naturalHeight;
        this.dialog.dataset.loading = 'false'; this.status.textContent = ''; this.fit();
      };
      full.onerror = () => {
        if (!this.opened || this.revision !== revision) return;
        this.dialog.dataset.loading = 'false';
        this.status.textContent = this.img.naturalWidth ? 'Full-size image unavailable; showing the preview.' : 'This image could not be loaded. Close the viewer and try again.';
      };
      full.src = source.src;
    }
    close() {
      if (!this.opened) return;
      this.opened = false;
      if (this.native && this.dialog.open) this.dialog.close();
      else this.dialog.removeAttribute('open');
      this.cleanup();
    }
    cleanup() {
      if (this.opened && this.dialog.open) return; // Ignore a previous queued close.
      this.opened = false; this.revision++;
      this.pointers.clear(); this.gesture = null; this.lastTap = null;
      this.img.onload = this.img.onerror = null; this.img.removeAttribute('src');
      if (this.full) { this.full.onload = this.full.onerror = null; this.full = null; }
      if (!this.lock) return;
      this.inertSiblings?.forEach(([node, inert]) => { node.inert = inert; }); this.inertSiblings = null;
      const lock = this.lock; this.lock = null;
      restore(document.body, lock.body);
      // Disable smooth scrolling until the exact pre-open position is restored.
      const scrollBehavior = lock.html.filter(([key]) => key === 'scroll-behavior');
      restore(document.documentElement, lock.html.filter(([key]) => key !== 'scroll-behavior'));
      window.scrollTo(lock.x, lock.y);
      this.trigger?.isConnected && this.trigger.focus({ preventScroll:true });
      restore(document.documentElement, scrollBehavior);
    }
    point(event) {
      const r = this.stage.getBoundingClientRect();
      return { x:event.clientX - r.left - r.width / 2, y:event.clientY - r.top - r.height / 2 };
    }
    fit() {
      if (!this.opened || !this.naturalWidth || !this.stage.clientWidth) return;
      this.width = this.stage.clientWidth; this.height = this.stage.clientHeight;
      const ratio = Math.min((this.width - 16) / this.naturalWidth, (this.height - 16) / this.naturalHeight);
      this.baseWidth = Math.max(1, this.naturalWidth * ratio); this.baseHeight = Math.max(1, this.naturalHeight * ratio);
      this.img.style.width = `${this.baseWidth}px`; this.img.style.height = `${this.baseHeight}px`;
      this.paint();
    }
    paint() {
      const limitX = Math.max(0, ((this.baseWidth || 0) * this.scale - (this.width || 0)) / 2);
      const limitY = Math.max(0, ((this.baseHeight || 0) * this.scale - (this.height || 0)) / 2);
      this.x = clamp(this.x, -limitX, limitX); this.y = clamp(this.y, -limitY, limitY);
      this.img.style.transform = `translate3d(${this.x}px, ${this.y}px, 0) scale(${this.scale})`;
      this.resetButton.textContent = `${Math.round(this.scale * 100)}%`;
      this.outButton.disabled = this.scale <= 1.001; this.inButton.disabled = this.scale >= 5.999;
      this.stage.classList.toggle('is-zoomed', this.scale > 1.001);
    }
    reset() { this.scale = 1; this.x = this.y = 0; this.paint(); }
    zoomAt(scale, point = {x:0, y:0}) {
      const next = clamp(scale, 1, 6), ratio = next / this.scale;
      this.x = point.x - (point.x - this.x) * ratio;
      this.y = point.y - (point.y - this.y) * ratio;
      this.scale = next; this.paint();
    }
    doubleTap(point) { this.scale > 1.05 ? this.reset() : this.zoomAt(2.5, point); }
    rebase() {
      const points = Array.from(this.pointers.values());
      if (points.length >= 2) {
        const [a, b] = points;
        const mid = { x:(a.x + b.x) / 2, y:(a.y + b.y) / 2 };
        this.gesture = { type:'pinch', distance:Math.max(1, Math.hypot(a.x - b.x, a.y - b.y)), scale:this.scale,
          anchorX:(mid.x - this.x) / this.scale, anchorY:(mid.y - this.y) / this.scale };
      } else if (points.length) this.gesture = { type:'pan', start:points[0], x:this.x, y:this.y };
      else this.gesture = null;
    }
    down(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      event.preventDefault();
      this.pointers.set(event.pointerId, this.point(event));
      if (this.pointers.size === 1) this.tap = { x:event.clientX, y:event.clientY, at:performance.now(), moved:false, multiple:false };
      else if (this.tap) this.tap.multiple = true;
      try { this.stage.setPointerCapture(event.pointerId); } catch (_) { /* Synthetic/older events. */ }
      this.rebase();
    }
    move(event) {
      if (!this.pointers.has(event.pointerId)) return;
      event.preventDefault();
      this.pointers.set(event.pointerId, this.point(event));
      if (this.tap && Math.hypot(event.clientX - this.tap.x, event.clientY - this.tap.y) > 8) this.tap.moved = true;
      const points = Array.from(this.pointers.values()), g = this.gesture;
      if (!g) return;
      if (points.length >= 2 && g.type === 'pinch') {
        const [a, b] = points, scale = clamp(g.scale * Math.hypot(a.x - b.x, a.y - b.y) / g.distance, 1, 6);
        this.scale = scale; this.x = (a.x + b.x) / 2 - g.anchorX * scale; this.y = (a.y + b.y) / 2 - g.anchorY * scale;
      } else if (g.type === 'pan' && this.scale > 1) {
        this.x = g.x + points[0].x - g.start.x; this.y = g.y + points[0].y - g.start.y;
      }
      this.paint();
    }
    up(event) {
      if (!this.pointers.has(event.pointerId)) return;
      this.pointers.delete(event.pointerId);
      if (!this.pointers.size && event.type === 'pointerup' && event.pointerType !== 'mouse' && this.tap && !this.tap.moved && !this.tap.multiple && performance.now() - this.tap.at < 350) {
        const now = performance.now(), p = this.point(event);
        if (this.lastTap && now - this.lastTap.at < 330 && Math.hypot(p.x - this.lastTap.x, p.y - this.lastTap.y) < 30) {
          this.doubleTap(p); this.lastTap = null; this.touchTapAt = now;
        } else this.lastTap = {...p, at:now};
      } else this.lastTap = null;
      this.rebase();
    }
    key(event) {
      if (event.key === 'Escape') { event.preventDefault(); this.close(); return; }
      if (event.key === 'Tab') {
        const controls = Array.from(this.dialog.querySelectorAll('button:not(:disabled), [tabindex="0"]'));
        const index = controls.indexOf(document.activeElement);
        if ((event.shiftKey && index <= 0) || (!event.shiftKey && index === controls.length - 1)) {
          event.preventDefault(); controls[event.shiftKey ? controls.length - 1 : 0].focus();
        }
        return;
      }
      if (['+', '=', '-', '0', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
        event.preventDefault();
        if (event.key === '0') this.reset();
        else if (event.key === '-' || event.key === '+' || event.key === '=') this.zoomAt(event.key === '-' ? this.scale / 1.5 : this.scale * 1.5);
        else {
          if (event.key === 'ArrowLeft') this.x += 40;
          if (event.key === 'ArrowRight') this.x -= 40;
          if (event.key === 'ArrowUp') this.y += 40;
          if (event.key === 'ArrowDown') this.y -= 40;
          this.paint();
        }
      }
    }
  }

  function targetFor(img) {
    if (!(img instanceof HTMLImageElement) || !img.isConnected || !img.matches(scope)) return null;
    // Even inside a gallery, existing links and functional controls must retain
    // native behavior. Never hijack image-file anchors, swatches or thumbnails.
    if (img.closest('a[href], button, label, summary, [role="radio"], [role="option"], dialog, [hidden], [inert], [data-mjl-no-zoom], [aria-hidden="true"], [data-gallery-media], [data-gallery-embed]')) return null;
    if (!safeURL(img.getAttribute('src') || img.currentSrc)) return null;
    const gallery = img.closest(galleryScope);
    const target = img.closest('[data-gallery-stage], [data-artwork-frame]');
    if (!gallery || !target || target.closest('mjl-product-gallery') !== gallery) return null;
    if (target.hasAttribute('data-gallery-stage') && gallery.current && gallery.current.type !== 'image') return null;
    return target;
  }
  function openImage(img, trigger) {
    if (!mobile.matches || !targetFor(img)) return;
    const source = sourceFor(img);
    if (!source.src) return;
    viewer ||= new ImageViewer();
    viewer.open(source, trigger);
  }
  function restoreTarget(target, record) {
    target.classList.remove('mjl-mobile-image-target');
    for (const [key, value] of Object.entries(record.original)) value === null ? target.removeAttribute(key) : target.setAttribute(key, value);
    records.delete(target);
  }
  function enhance(img) {
    const target = targetFor(img);
    if (!target) return;
    if (!records.has(target)) {
      const original = Object.fromEntries(['role','tabindex','aria-label','aria-haspopup'].map(name => [name, target.getAttribute(name)]));
      records.set(target, {img, original});
      // Accessibility semantics only: no visible button, icon, hint or cursor.
      target.classList.add('mjl-mobile-image-target'); target.setAttribute('role', 'button'); target.setAttribute('tabindex', '0'); target.setAttribute('aria-haspopup', 'dialog');
    } else records.get(target).img = img;
    target.setAttribute('aria-label', `Enlarge image: ${img.alt || img.closest(galleryScope)?.dataset.productTitle || 'product photo'}`);
  }
  function scan() {
    scanFrame = null;
    if (!mobile.matches) return;
    for (const node of dirty) {
      if (!node.isConnected) continue;
      if (node.matches?.('img')) enhance(node);
      node.querySelectorAll?.(scope).forEach(enhance);
    }
    dirty.clear();
    for (const [target, record] of records) {
      if (targetFor(record.img) !== target) restoreTarget(target, record);
    }
  }
  function queue(node) { dirty.add(node); if (!scanFrame) scanFrame = requestAnimationFrame(scan); }
  function activate() {
    observer?.disconnect();
    cancelAnimationFrame(scanFrame); scanFrame = null; dirty.clear();
    pagePointers.clear();
    if (!mobile.matches) {
      records.forEach((record, target) => restoreTarget(target, record));
      return;
    }
    const main = document.querySelector('main');
    if (!main) return;
    if (!observer) observer = new MutationObserver(changes => {
      for (const change of changes) {
        const gallery = change.target.closest?.(galleryScope);
        if (gallery) { queue(gallery); continue; }
        if (change.type !== 'childList') continue;
        // Section rendering may replace the gallery. Examine gallery roots
        // only; never scan/enhance arbitrary main-page or linked photographs.
        change.addedNodes.forEach(node => {
          if (node.nodeType !== 1) return;
          if (node.matches(galleryScope)) queue(node);
          node.querySelectorAll(galleryScope).forEach(queue);
        });
        if (change.removedNodes.length) {
          for (const [target, record] of records) {
            if (!target.isConnected || targetFor(record.img) !== target) restoreTarget(target, record);
          }
        }
      }
    });
    observer.observe(main, { childList:true, subtree:true, attributes:true, attributeFilter:['src','srcset'] });
    main.querySelectorAll(galleryScope).forEach(queue);
  }

  // Ignore clicks synthesized at the end of a scroll, gallery swipe or pinch.
  document.addEventListener('pointerdown', event => {
    if (!mobile.matches || !event.target.closest?.(galleryScope) || event.target.closest?.('dialog')) return;
    pagePointers.set(event.pointerId, {x:event.clientX, y:event.clientY, moved:false});
    if (pagePointers.size > 1) { ignoreClickUntil = performance.now() + 500; pagePointers.forEach(p => { p.moved = true; }); }
  }, {passive:true, capture:true});
  document.addEventListener('pointermove', event => {
    const p = pagePointers.get(event.pointerId);
    if (p && Math.hypot(event.clientX - p.x, event.clientY - p.y) > 12) p.moved = true;
  }, {passive:true, capture:true});
  ['pointerup','pointercancel'].forEach(type => document.addEventListener(type, event => {
    const p = pagePointers.get(event.pointerId);
    if (p && (p.moved || event.type === 'pointercancel' || Math.hypot(event.clientX - p.x, event.clientY - p.y) > 12)) ignoreClickUntil = performance.now() + 450;
    pagePointers.delete(event.pointerId);
  }, {passive:true, capture:true}));
  document.addEventListener('click', event => {
    if (!mobile.matches || event.defaultPrevented || event.target.closest?.('a[href], dialog')) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const enlarge = event.target.closest?.('[data-gallery-zoom]');
    if (enlarge) {
      const img = enlarge.closest(galleryScope)?.querySelector('[data-gallery-stage] img');
      if (img && targetFor(img)) { event.preventDefault(); event.stopImmediatePropagation(); openImage(img, enlarge); }
      return;
    }
    const target = event.target.closest?.('.mjl-mobile-image-target');
    const record = records.get(target);
    if (!record || targetFor(record.img) !== target) return;
    if (event.target !== target && event.target !== record.img) return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (performance.now() >= ignoreClickUntil) openImage(record.img, target);
  }, true);
  document.addEventListener('keydown', event => {
    if (!mobile.matches || !['Enter', ' '].includes(event.key)) return;
    const record = records.get(event.target);
    if (record && targetFor(record.img) === event.target) { event.preventDefault(); event.stopImmediatePropagation(); openImage(record.img, event.target); }
  }, true);
  document.addEventListener('load', event => { if (mobile.matches && event.target.matches?.(scope)) queue(event.target); }, true);
  document.addEventListener('shopify:section:load', activate);
  if (mobile.addEventListener) mobile.addEventListener('change', activate);
  else mobile.addListener(activate);
  window.MJLImageViewer = Object.freeze({open:openImage, close:() => viewer?.close()});
  activate();
})();
